import React, { useEffect, useState } from 'react';
import { InternalPageRoot, PdfOrImgViewer, ThemedButton, ThemedInput } from "../../../index";
import { useTheme } from "../../../../common/ProjectThemeProvider";
import {
    checkNullArr,
    checkNullStr,
    getArrLen,
    getCurrUrlMainDomain,
    getCurrUrlParamValue,
    getJsonValue,
    getJsonValueFromNestedKeys,
    isBoolTrue,
    isDateExpired,
    isJsonValueTrue
} from "../../../../common/fusion-ui/utils/ReactUtils";
import { formatDateValue, getUserSessionField, printError, printLog, sendRequest } from "../../../utils/AppUtils";
import { useToast } from "../../../../common/fusion-ui/provider/ToastProvider";
import { GridBox, Label, NullMsgLabel, Section } from "../../../../common/fusion-ui";
import { Constants, CssVariant, DefStrNA } from 'common/fusion-ui/config/FusionConfig';
import { EVENT_INPUT_MAX_LEN, GenRouteUrls, ModuleFieldsData, Modules, ReqUrls, ShareFeatureModules, ShowViewType } from "../../../config/AppConfig";
import { Messages } from '../../../../common/fusion-ui/config/FusionConfig';

const Forum_InternalPage = React.memo(({
    open,
    onHide,
    recordId,
    showAs = ShowViewType.PAGE,
    onPageUpdate,
}) => {
    const TAG = "Forum_InternalPage";

    const theme = useTheme();
    const { showToast, changeToastVariant } = useToast();
    const moduleName = Modules.DiscussionForum;
    const mf = ModuleFieldsData[moduleName];

    const Fields = {
        recordId: 'recordId',
        pageData: 'pageData',
        commentList: 'commentList',

        isDataLoading: 'isDataLoading',
        isDataFound: 'isDataFound',
        dataLoadingMsg: 'dataLoadingMsg',
        isDataFetched: 'isDataFetched',

        prevRecordId: 'prevRecordId',
        nextRecordId: 'nextRecordId',

        commentInputValue: 'commentInputValue',
        postSubmitBtn_disable: 'postSubmitBtn_disable',
        postSubmitBtn_asLoading: 'postSubmitBtn_asLoading',

        isSubscribed: 'isSubscribed',
        subBtn_disable: 'subBtn_disable',
        subBtn_asLoading: 'subBtn_asLoading',

        commentRepliesVisList: 'commentRepliesVisList',
    };

    const [fieldsData, setFieldsData] = useState({});

    useEffect(() => {
        // log('useEffect.showAs:', showAs, 'recordId:', recordId);
        if (showAs == ShowViewType.PAGE) {
            const id = getCurrUrlParamValue('id');
            fetchPageData(id, true);
        } else {
            if (checkNullStr(recordId)) {
                fetchPageData(recordId, false);
            }
        }
    }, [recordId, showAs]);

    return (<>
        <InternalPageRoot
            tag={TAG}
            moduleName={moduleName}
            title={`Forum Details`}
            backToListText={'Back to forums'}
            isDataFound={getField(Fields.isDataFound)}
            isDataLoading={getField(Fields.isDataLoading)}
            dataLoadingMsg={getField(Fields.dataLoadingMsg)}
            internalPageUrl={GenRouteUrls.df_internalPage}
            prevRecordId={getField(Fields.prevRecordId)}
            nextRecordId={getField(Fields.nextRecordId)}
            onPrev={() => fetchPageData(getField(Fields.prevRecordId), false)}
            onNext={() => fetchPageData(getField(Fields.nextRecordId), false)}
            showShare={ShareFeatureModules.some((item) => item.tag == TAG)}
            shareUrl={`${getCurrUrlMainDomain()}${GenRouteUrls.df_internalPage}${getField(Fields.recordId)}`}
            open={open}
            onHide={onHide}
            drawerWidth={'70%'}
            showAs={showAs}>
            <div>
                <GridBox className={'px-2'} sm={12} md={[7, 5]} gap={1}>
                    {/* records */}
                    <div>
                        <table className={'w-100x'} style={{ color: theme.text }}>
                            <tbody>
                                {/* Updated by Vandit Tewari on (6/11/24) - in every tr given a class 'wmInternalPageTrBreak' done this bcz below 560px the text in some td getting out of screen as these both comes in same row - declared in fusion css also in all td added class text-break*/}
                                {/*title*/}
                                <tr className='wmInternalPageTrBreak'>
                                    <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.title.titleAlt}:</th>
                                    <td className={'px-2 text-wrap text-break text-break'}>{getPageField(mf.title, DefStrNA)}</td>
                                </tr>

                                {/* Discussion led by */}
                                <tr className='wmInternalPageTrBreak'>
                                    <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.leader.titleAlt}:</th>
                                    <td className={'px-2 text-wrap text-break'}>{getPageField(mf.leader, DefStrNA)}</td>
                                </tr>

                                {/* startDate  */}
                                <tr className='wmInternalPageTrBreak'>
                                    <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.startDate.title}:</th>
                                    <td className={'px-2 text-wrap text-break'}>{formatDateValue(getPageField(mf.startDate, DefStrNA))}</td>
                                </tr>

                                {/* Forum Open till */}
                                <tr className='wmInternalPageTrBreak'>
                                    <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.endDate.titleAlt2}:</th>
                                    <td className={'px-2 text-wrap text-break'}>{formatDateValue(getPageField(mf.endDate, DefStrNA))}</td>
                                </tr>

                                {/* Topic description */}
                                <tr className={'mt-1 wmInternalPageTrBreak'}>
                                    <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.desc.title}:</th>
                                    <td className={'px-2 text-break'}>
                                        <div className={'text-wrap'}
                                            dangerouslySetInnerHTML={{ __html: getPageField(mf.desc, DefStrNA) }} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        {/* sub/sub notif btn */}
                        <div className={'mt-2'}>
                            <ThemedButton
                                className={'px-3 py-1 text-nowrap w-auto'}
                                text={isFieldTrue(Fields.subBtn_asLoading)
                                    ? (`${isFieldTrue(Fields.isSubscribed) ? 'Unsubscribing' : 'Subscribing'} notifications...`)
                                    : (`${isFieldTrue(Fields.isSubscribed) ? 'Unsubscribe' : 'Subscribe'} notifications`)}
                                outlined={true}
                                disableVariant={'xl'}
                                variant={theme.primaryBlue}
                                bgColor={theme.primaryBlue20}
                                borderColor={'transparent'}
                                asLoading={isFieldTrue(Fields.subBtn_asLoading)}
                                disabled={isFieldTrue(Fields.subBtn_disable)}
                                onClick={() => {
                                    if (isFieldTrue(Fields.isSubscribed)) {
                                        if (window.confirm('Are you sure you want to unsubscribe notifications?')) {
                                            handleSubUnsubNotifs();
                                        }
                                    } else {
                                        handleSubUnsubNotifs();
                                    }
                                }}
                            />
                        </div>


                        {/* Document viewer */}
                        <div className={'my-3'}>
                            <PdfOrImgViewer
                                src={getPageField(mf.doc)}
                                fileExt={getPageField(mf.docFileExt)}
                            />
                        </div>
                    </div>

                    {/* post, comments-list */}
                    <div>
                        {/* comment-input */}
                        <div>
                            {/*input*/}
                            <ThemedInput
                                className={''}
                                label={`New Comment:`}
                                labelClassName={`fw-bold text-nowrap `}
                                asTextArea={true}
                                variant={false}
                                value={getField(Fields.commentInputValue)}
                                disabled={isDateExpired(getPageField(mf.endDate))}
                                placeholder={isDateExpired(getPageField(mf.endDate)) ? 'This Forum is closed for comments.' : 'Add your comment'}
                                style={{ height: '80px', minHeight: '40px', maxHeight: '260px' }}
                                maxLength={EVENT_INPUT_MAX_LEN / 4}
                                showLimitLabel={true}
                                // showMsg={false}
                                onChange={(e) => updateField(Fields.commentInputValue, e.target.value)}
                            />
                            <div className="d-flex w-auto mt-n2">
                                <ThemedButton
                                    className={'px-2 py-1 text-nowrap w-auto fs-sm-md'}
                                    text={isFieldTrue(Fields.postSubmitBtn_asLoading) ? 'Posting comment...' : 'Post your comment'}
                                    outlined={true}
                                    disableVariant={'xl'}
                                    variant={theme.primaryBlue}
                                    asLoading={isFieldTrue(Fields.postSubmitBtn_asLoading)}
                                    disabled={isFieldTrue(Fields.postSubmitBtn_disable) || isDateExpired(getPageField(mf.endDate))}
                                    onClick={handlePostSubmit}
                                />
                            </div>
                        </div>

                        {/* Comment list */}
                        <div
                            className={'mt-4 h-auto px-0 pt-2x pb-0'}>
                            {/*comment-heading*/}
                            <Label className={"fw-semi-bold px-3x"} color={theme.text60} textAt={'start'}>
                                {`Comments${checkNullArr(getCommentsList()) ? ` (${getArrLen(getCommentsList())})` : ``}`}
                            </Label>

                            {/*comment-list*/}
                            {checkNullArr(getCommentsList())
                                ? <div className={'mt-2 px-3x pb-3 scroller overflow-auto scroll-dark'}
                                    style={{ maxHeight: '65vh' }}>{getCommentsList().map((cmtItem, cmtIndex) => {
                                        return <Section
                                            key={cmtIndex}
                                            className={`px-2 py-1 app-section hover ${cmtIndex > 0 ? 'mt-3' : ''}`}
                                            borderColor={theme.text20}
                                            roundedVariant={3}>
                                            <div>
                                                {/*comment*/}
                                                <div>
                                                    {/*name & date*/}
                                                    <div>
                                                        <Label className={'m-0 p-0 fw-bold text-wrap flex-wrap'} color={theme.text80} textAt={'start'}>
                                                            {/*name*/}
                                                            <span className={'text-nowrap'}>{getJsonValue(cmtItem, mf.comment_memberName.column, DefStrNA)}</span>

                                                            <span>&nbsp;&nbsp;</span>

                                                            {/*date*/}
                                                            <span className={'fw-semi-bold text-nowrap fs-sm'} style={{ color: theme.text50 }}>
                                                                {formatDateValue(getJsonValue(cmtItem, mf.comment_insertDate.column, DefStrNA), Constants.DD_MM_YYYY_DASH)}
                                                            </span>
                                                        </Label>
                                                    </div>

                                                    {/*text*/}
                                                    <div>
                                                        <Label className={'m-0 p-0 text-wrap'} color={theme.text} textAt={'start'}>
                                                            {getJsonValue(cmtItem, mf.comment_commentText.column, DefStrNA)}
                                                        </Label>
                                                    </div>
                                                </div>

                                                {/*replies*/}
                                                {checkNullArr(getJsonValue(cmtItem, mf.comment_replyList.column))
                                                    && <div className={'mt-n1x'}>
                                                        {/*show-replies btn*/}
                                                        <div>
                                                            <ThemedButton
                                                                className={'px-1 rounded-10 py-1 text-nowrap fw-semi-bold w-auto fs-sm'}
                                                                text={'Show Replies'}
                                                                outlined={true}
                                                                borderWidth={0}
                                                                height={'18px'}
                                                                ml={'n1'}
                                                                mb={1}
                                                                variant={theme.primaryBlue}
                                                                hoverBgColor={theme.primaryBlue20}
                                                                hoverColor={theme.primaryBlue}
                                                                onClick={() => {
                                                                    const fun = 'comments.map.showReply.onClick:';
                                                                    try {
                                                                        let visList = getField(Fields.commentRepliesVisList);
                                                                        visList = checkNullArr(visList) ? visList : [];
                                                                        // log(fun, 'visList-before:', visList);

                                                                        if (visList.includes(cmtIndex)) {
                                                                            visList = visList.filter(item => item != cmtIndex);
                                                                        } else {
                                                                            visList.push(cmtIndex);
                                                                        }
                                                                        // log(fun, 'visList:', visList);
                                                                        updateField(Fields.commentRepliesVisList, visList);
                                                                    } catch (error) {
                                                                        logErr(fun, error);
                                                                    }
                                                                }}
                                                            />
                                                        </div>

                                                        {/*reply-list*/}
                                                        {(checkNullArr(getField(Fields.commentRepliesVisList)) && getField(Fields.commentRepliesVisList).includes(cmtIndex)) &&
                                                            <div
                                                                className={'ps-3 mb-2'}>{getJsonValue(cmtItem, mf.comment_replyList.column).map((replItem, rplIndex) => {
                                                                    return <Section
                                                                        key={rplIndex}
                                                                        className={`px-2 py-1 app-section hover ${rplIndex > 0 ? 'mt-2' : ''}`}
                                                                        backgroundColor={theme.text05}
                                                                        borderColor={theme.text20}
                                                                        roundedVariant={3}>
                                                                        <div>
                                                                            {/*name & date*/}
                                                                            <div>
                                                                                <Label className={'m-0 p-0 fw-bold'} color={theme.text80} textAt={'start'}>
                                                                                    {/*name*/}
                                                                                    <span>{getJsonValue(replItem, mf.commentReply_memberName.column, DefStrNA)}</span>

                                                                                    <span>&nbsp;&nbsp;</span>

                                                                                    {/*date*/}
                                                                                    <span className={'fw-semi-bold text-nowrap fs-sm-md'} style={{ color: theme.text50 }}>
                                                                                        {formatDateValue(getJsonValue(replItem, mf.commentReply_insertDate.column, DefStrNA), Constants.DD_MM_YYYY_DASH)}
                                                                                    </span>
                                                                                </Label>
                                                                            </div>

                                                                            {/*text*/}
                                                                            <div>
                                                                                <Label className={'m-0 p-0 text-wrap'} color={theme.text} textAt={'start'}>
                                                                                    {getJsonValue(replItem, mf.commentReply_commentText.column, DefStrNA)}
                                                                                </Label>
                                                                            </div>
                                                                        </div>
                                                                    </Section>
                                                                })}</div>}
                                                    </div>}
                                            </div>
                                        </Section>
                                    })}</div>
                                : <NullMsgLabel className={'mb-2 px-3x'} textAt={'start'} color={theme.text80}>No comments found!</NullMsgLabel>}
                        </div>
                    </div>
                </GridBox>
            </div>
        </InternalPageRoot>
    </>)

    //======================Request Functions start=====================
    async function fetchPageData(recordId, checkBefore = true) {
        const fun = 'fetchPageData:';
        if (!checkBefore || (checkBefore && !isFieldTrue(Fields.isDataFetched))) {
            // log(fun, 'recordId:', recordId);
            updateField(Fields.isDataFetched, true);
            updateField(Fields.recordId, recordId);
            if (checkNullStr(recordId)) {
                updateField(Fields.pageData, {});
                updateField(Fields.isDataFound, false);
                updateField(Fields.isDataLoading, true);
                updateField(Fields.dataLoadingMsg, "");
                updateField(Fields.nextRecordId, null);
                updateField(Fields.prevRecordId, null);
                updateField(Fields.isSubscribed, null);

                const REQUEST_OPTIONS = {
                    recordId: recordId,
                    getAdjacentRecordIds: true,
                    getComments: true,
                    checkSub: true,
                };
                try {
                    sendRequest({
                        reqUrl: ReqUrls.df__addEditPage_fetch_editFormData,
                        reqOptions: REQUEST_OPTIONS,
                        onFetched: ((fetchedData) => {
                            log(fun, 'fetchedData:', fetchedData);

                            const pageData = getJsonValueFromNestedKeys(fetchedData, ['data', 'pageData'], {});
                            const comments = getJsonValueFromNestedKeys(fetchedData, ['data', 'comments'], []);
                            const isSubscribed = getJsonValueFromNestedKeys(fetchedData, ['data', 'isSubscribed']);
                            const success = isJsonValueTrue(fetchedData, 'success');
                            const message = getJsonValue(fetchedData, 'message');

                            if (success) {
                                // toast(message, CssVariant.success)
                                updateField(Fields.pageData, pageData);

                                updateField(Fields.nextRecordId, getJsonValue(pageData, 'nextRecordId'));
                                updateField(Fields.prevRecordId, getJsonValue(pageData, 'prevRecordId'));

                                updateField(Fields.commentList, comments);
                                updateField(Fields.isSubscribed, isSubscribed);
                            } else {
                                toast(message, CssVariant.danger)
                                updateField(Fields.dataLoadingMsg, message);
                            }
                            updateField(Fields.isDataFound, success);
                            updateField(Fields.isDataLoading, false);
                        }),
                        onError: ((error) => {
                            logErr(fun, error);
                            toast(Messages.fetchError, CssVariant.danger)
                            updateField(Fields.dataLoadingMsg, Messages.fetchError);
                            updateField(Fields.isDataFound, false);
                            updateField(Fields.isDataLoading, false);
                        }),
                    });
                } catch (error) {
                    toast(Messages.fetchError, CssVariant.danger)
                    logErr(fun, error);
                    updateField(Fields.dataLoadingMsg, Messages.fetchError);
                    updateField(Fields.isDataFound, false);
                    updateField(Fields.isDataLoading, false);
                }
            } else {
                toast('Record-id not found!', CssVariant.danger);
                updateField(Fields.dataLoadingMsg, 'Record-id not found!');
                updateField(Fields.isDataFound, false);
                updateField(Fields.isDataLoading, false);
            }
        }
    }

    async function handleSubUnsubNotifs() {
        const fun = 'handleSubUnsubNotifs:';
        try {
            updateField(Fields.subBtn_asLoading, true);
            updateField(Fields.subBtn_disable, true);

            const isSubscribed = isFieldTrue(Fields.isSubscribed);
            log(fun, 'isSubscribed:', isSubscribed);

            const REQ_OPTIONS = { isSubscribed: !isSubscribed };
            sendRequest({
                reqUrl: ReqUrls.df__internalPage_subUnsubNotifs,
                reqOptions: REQ_OPTIONS,
                onFetched: ((fetchedData) => {
                    log(fun, 'fetchedData:', fetchedData);
                    const success = isJsonValueTrue(fetchedData, 'success');
                    const message = getJsonValue(fetchedData, 'message');

                    if (success) {
                        updateField(Fields.isSubscribed, !isSubscribed);
                        toast(message, CssVariant.success);
                    } else {
                        toast(message, CssVariant.danger);
                    }
                    updateField(Fields.subBtn_asLoading, false);
                    updateField(Fields.subBtn_disable, false);
                }),
                onError: ((error) => {
                    logErr(fun, error);
                    toast('Request failed, please try again', CssVariant.danger);
                    updateField(Fields.subBtn_asLoading, false);
                    updateField(Fields.subBtn_disable, false);
                }),
            });
        } catch (error) {
            logErr(fun, error);
            toast('Request failed, please try again', CssVariant.danger);
            updateField(Fields.subBtn_asLoading, false);
            updateField(Fields.subBtn_disable, false);
        }
    }

    async function handlePostSubmit() {
        const fun = 'handlePostSubmit:';
        try {
            const commentText = getField(Fields.commentInputValue);
            //null-checks
            if (!checkNullStr(commentText)) {
                toast('Comment cannot be empty!', CssVariant.warning);
                return false;
            }
            if (isDateExpired(getPageField(mf.endDate))) {
                toast(`Can't add comments for a closed forum!`, CssVariant.warning);
                return false;
            }

            const proceed = true;
            if (proceed) {
                updateField(Fields.postSubmitBtn_asLoading, true);
                updateField(Fields.postSubmitBtn_disable, true);

                const REQ_OPTIONS = {
                    recordId: getField(Fields.recordId),
                    commentText: commentText,
                };

                sendRequest({
                    reqUrl: ReqUrls.df__internalPage_submitComment,
                    reqOptions: REQ_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);
                        const success = isJsonValueTrue(fetchedData, 'success');
                        const message = getJsonValue(fetchedData, 'message');

                        if (success) {
                            toast(message, CssVariant.success);

                            //add this comment in the comment-list
                            try {
                                let commentList = getField(Fields.commentList);
                                commentList = checkNullArr(commentList) ? commentList : [];
                                commentList.push({
                                    [mf.comment_memberName.column]: getUserSessionField('fullName'),
                                    [mf.comment_commentText.column]: commentText,
                                    [mf.comment_insertDate.column]: new Date(),
                                });

                                updateField(Fields.commentInputValue, '');
                                updateField(Fields.commentList, commentList);
                            } catch (error) {
                                toast(`Couldn't add the comment in the list, pleases refresh the page to show all comments.`, CssVariant.danger);
                                logErr(fun, error);
                            }
                            if (onPageUpdate) onPageUpdate();
                        } else {
                            toast(message, CssVariant.danger);
                        }
                        updateField(Fields.postSubmitBtn_asLoading, false);
                        updateField(Fields.postSubmitBtn_disable, false);
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        toast(`Comment failed, please try again`, CssVariant.danger);
                        updateField(Fields.postSubmitBtn_asLoading, false);
                        updateField(Fields.postSubmitBtn_disable, false);
                    }),
                });
            }
        } catch (error) {
            logErr(fun, error);
            toast(`Comment failed, please try again`, CssVariant.danger);
            updateField(Fields.postSubmitBtn_asLoading, false);
            updateField(Fields.postSubmitBtn_disable, false);
        }
    }

    //======================Request Functions end=====================

    //======================Util Functions start=====================

    function getPageField(colField, defValue) {
        return getJsonValue(getField(Fields.pageData), colField.column, defValue);
    }

    function getCommentsList() {
        return checkNullArr(getField(Fields.commentList)) ? getField(Fields.commentList) : [];
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({ ...prevState, [field]: value }));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default Forum_InternalPage;
